import { Component, Inject, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MaterialSharedModule } from '@app/shared/modules/material-shared.module';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NotificationAction, getButtonsForType } from '@app/core/infrastructure/notification';

export interface NotificationActionDialogData {
  notification_id: string;
  type: string;
  notification_type?: string;
  reminder_category?: string;
  title: string;
  body: string;
  route?: string;
  actions?: NotificationAction[];
}

export interface NotificationActionDialogResult {
  action_id: string;  // The id of the action button clicked
  notification_id: string;
  type: string;
  route?: string;
}

@Component({
  selector: 'app-notification-action-dialog',
  templateUrl: './notification-action-dialog.component.html',
  styleUrls: ['./notification-action-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MaterialSharedModule,
    TranslateModule
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class NotificationActionDialogComponent {
  /** Buttons determined by notification type */
  readonly actions: NotificationAction[];

  constructor(
    public dialogRef: MatDialogRef<NotificationActionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: NotificationActionDialogData,
    private translate: TranslateService
  ) {
    this.actions = data.actions ?? this.getDefaultActions(data);
  }

  private getDefaultActions(data: NotificationActionDialogData): NotificationAction[] {
    if (data.notification_type === 'check_in' || data.reminder_category === 'check_in') {
      return [];
    }

    return getButtonsForType(data.type);
  }

  /**
   * Sort actions: secondary buttons first (left), primary buttons last (right)
   */
  get sortedActions(): NotificationAction[] {
    return [...this.actions].sort((a, b) => {
      const aIsPrimary = this.isPrimary(a);
      const bIsPrimary = this.isPrimary(b);
      if (aIsPrimary && !bIsPrimary) return 1;
      if (!aIsPrimary && bIsPrimary) return -1;
      return 0;
    });
  }

  get iconName(): string {
    switch (this.data.type) {
      case 'reminder':
        return 'notifications_active';
      case 'announcement':
        return 'campaign';
      case 'subscription':
        return 'credit_card';
      case 'feature':
        return 'new_releases';
      case 'system':
        return 'warning';
      case 'matching':
        return 'people';
      default:
        return 'notifications';
    }
  }

  /**
   * Get the icon for an action button
   */
  getActionIcon(actionId: string): string {
    switch (actionId) {
      case 'accept': return 'check';
      case 'dismiss': return 'close';
      case 'view': return 'visibility';
      case 'later': return 'schedule';
      case 'confirm': return 'check_circle';
      case 'cancel': return 'cancel';
      default: return 'touch_app';
    }
  }

  /**
   * Get translation key for action button
   * Falls back to the title from payload, then to capitalized action ID
   */
  getActionLabel(action: NotificationAction): string {
    const translationKey = `notifications.actions.${action.id}`;
    const translated = this.translate.instant(translationKey);
    // If translation key not found, use the title from payload
    if (translated !== translationKey) return translated;
    // If title exists, use it
    if (action.title) return action.title;
    // Last resort: capitalize the action ID
    return action.id.charAt(0).toUpperCase() + action.id.slice(1);
  }

  /**
   * Check if action is primary style
   */
  isPrimary(action: NotificationAction): boolean {
    // Check style first, fallback to ID-based detection
    if (action.style === 'primary') return true;
    if (action.style === 'secondary' || action.style === 'destructive') return false;
    // Fallback: treat accept/confirm/view as primary
    return ['accept', 'confirm', 'view'].includes(action.id);
  }

  /**
   * Handle action button click
   */
  onAction(action: NotificationAction): void {
    const result: NotificationActionDialogResult = {
      action_id: action.id,
      notification_id: this.data.notification_id,
      type: this.data.type,
      route: this.data.route
    };
    this.dialogRef.close(result);
  }
}
